import { useTracking, getText } from '../utils';
import { onRoleButtonKeyDown } from '../utils';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Close } from '@gjensidige/builders-icons';
import type { TrackingProp } from '../types';
import styles from './filter.module.css';
import clsx from 'clsx';

type Clickable = {
  closeLabel?: never;
  closeProps?: never;
  onClose?: never;
  selected?: boolean;
  tracking?: TrackingProp;
};

type Closeable = {
  closeLabel: string;
  closeProps?: React.ComponentPropsWithoutRef<'button'>;
  onClose: React.MouseEventHandler<HTMLButtonElement>;
  selected?: never;
  tracking?: TrackingProp;
};

export type FilterProps = React.ComponentPropsWithoutRef<'span'> &
  (Clickable | Closeable);

export const Filter = forwardRef<HTMLSpanElement, FilterProps>(function Filter(
  {
    children,
    className,
    closeLabel,
    closeProps,
    onClick,
    onClose,
    selected,
    tracking = true,
    ...rest
  },
  ref
) {
  useEffect(() => {
    console.warn(
      'Warning: <Filter> is deprecated and will be removed at the beginning of June. Please use <Toggles> instead.'
    );
  }, []);

  const clickable = !!(onClick && !onClose) || undefined;
  const closeable = !!(onClose && closeLabel);
  const innerRef = useRef<HTMLDivElement>(null);
  const track = useTracking(tracking);

  useImperativeHandle(ref, () => innerRef.current as HTMLDivElement); // Forward innerRef

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    track({
      eventCategory: 'filter',
      eventAction: 'click',
      eventLabel: getText(innerRef.current)
    });
    onClick?.(event); // Track before onClick in case URL changes
  };

  const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
    track({
      eventCategory: 'filter',
      eventAction: 'close',
      eventLabel: getText(innerRef.current)
    });
    onClose?.(event); // Track before onClose in case URL changes
  };

  return (
    <span
      aria-selected={selected ? true : undefined}
      className={clsx(styles.filter, closeable && styles.closeable, className)}
      onKeyDown={clickable && onRoleButtonKeyDown}
      onClick={clickable && handleClick}
      role={clickable && 'button'}
      ref={innerRef}
      tabIndex={clickable && 0}
      {...rest}
    >
      {children}
      {closeable && (
        <button
          {...closeProps}
          aria-label={closeLabel}
          className={clsx(styles.close, closeProps?.className)}
          onClick={handleClose}
          type="button"
        >
          <Close width={16} height={16} />
        </button>
      )}
    </span>
  );
});
